import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPhotos = ({ user }) => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null,
    title: '',
    category: ''
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  const categories = [
    { value: 'ear', label: 'Ear' },
    { value: 'facial', label: 'Facial' },
    { value: 'oral', label: 'Oral' },
    { value: 'surface', label: 'Surface' },
    { value: 'body', label: 'Body' }
  ];

  useEffect(() => {
    if (!user && !localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
    loadPhotos();
  }, [user, navigate]);

  const loadPhotos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/photos`);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error loading photos:', error);
      toast({
        title: "Error loading photos",
        description: "Could not load your photos.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file",
          description: "Please select an image file.",
          variant: "destructive"
        });
        return;
      }
      
      setUploadData({ ...uploadData, file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file || !uploadData.title || !uploadData.category) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields and select a photo.",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('title', uploadData.title);
      formData.append('category', uploadData.category);

      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/photos/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: "Photo uploaded!",
        description: "Your photo has been added to the gallery.",
      });

      // Reset form
      setUploadData({ file: null, title: '', category: '' });
      setPreviewUrl(null);
      document.getElementById('photo-upload').value = '';
      
      // Reload photos
      loadPhotos();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error.response?.data?.detail || "Failed to upload photo.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/photos/${photoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      toast({
        title: "Photo deleted",
        description: "Photo removed from gallery.",
      });

      loadPhotos();
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Delete failed",
        description: "Could not delete photo.",
        variant: "destructive"
      });
    }
  };

  if (!user && !localStorage.getItem('user')) {
    return null;
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black mb-4 text-zinc-100">MANAGE PHOTOS</h1>
          <p className="text-zinc-400 text-lg">Upload and manage your piercing gallery</p>
        </div>

        {/* Upload Form */}
        <div className="bg-zinc-900 border border-zinc-800 p-8 mb-12">
          <h2 className="text-2xl font-bold text-zinc-100 mb-6 flex items-center gap-2">
            <Upload className="h-6 w-6" />
            Upload New Photo
          </h2>
          
          <form onSubmit={handleUpload} className="space-y-6">
            <div>
              <Label htmlFor="photo-upload" className="text-zinc-100 font-bold mb-2 block">
                Select Photo
              </Label>
              <Input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="bg-zinc-950 border-zinc-700 text-zinc-100"
              />
            </div>

            {previewUrl && (
              <div className="border border-zinc-700 p-4 bg-zinc-950">
                <img src={previewUrl} alt="Preview" className="max-h-64 mx-auto" />
              </div>
            )}

            <div>
              <Label htmlFor="title" className="text-zinc-100 font-bold mb-2 block">
                Title/Description
              </Label>
              <Input
                id="title"
                type="text"
                value={uploadData.title}
                onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                placeholder="e.g., Septum Piercing, Industrial Bar, etc."
                className="bg-zinc-950 border-zinc-700 text-zinc-100"
              />
            </div>

            <div>
              <Label htmlFor="category" className="text-zinc-100 font-bold mb-2 block">
                Category
              </Label>
              <Select
                value={uploadData.category}
                onValueChange={(value) => setUploadData({ ...uploadData, category: value })}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700">
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-zinc-100">
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={isUploading}
              className="w-full bg-red-800 hover:bg-red-700 text-white py-6 text-lg font-bold"
            >
              {isUploading ? 'Uploading...' : 'Upload Photo'}
            </Button>
          </form>
        </div>

        {/* Current Photos */}
        <div>
          <h2 className="text-3xl font-bold text-zinc-100 mb-6">Your Gallery ({photos.length} photos)</h2>
          
          {isLoading ? (
            <div className="text-center text-zinc-400 py-12">Loading photos...</div>
          ) : photos.length === 0 ? (
            <div className="text-center text-zinc-400 py-12 bg-zinc-900 border border-zinc-800">
              <ImageIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p>No photos yet. Upload your first one above!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div key={photo.id} className="relative group bg-zinc-900 border border-zinc-800 overflow-hidden">
                  <img
                    src={`${BACKEND_URL}${photo.url}`}
                    alt={photo.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-3">
                    <p className="text-zinc-100 font-semibold text-sm truncate">{photo.title}</p>
                    <p className="text-zinc-500 text-xs capitalize">{photo.category}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(photo.id)}
                    className="absolute top-2 right-2 bg-red-800 hover:bg-red-700 text-white p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPhotos;
